import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Image from '@tiptap/extension-image';
import FontFamily from '@tiptap/extension-font-family';
import FontSize from '@tiptap/extension-font-size';
import Highlight from '@tiptap/extension-highlight';
import { Extension } from '@tiptap/core';
import { Input, Button, ButtonGroup, Tooltip, Select, Popover, Modal } from '@douyinfe/semi-ui';
import { IconBold, IconItalic, IconUnderline, IconStrikeThrough, IconAlignLeft, IconAlignCenter, IconAlignRight, IconFont, IconFontColor, IconMark, IconImage, IconCode } from '@douyinfe/semi-icons';
import ImageSelector from '../ImageSelector';
import './index.scss';
import { createRoot } from 'react-dom/client';
import { IconLineHeight, IconText, IconHn, Icon } from '@douyinfe/semi-icons';
import { IconSpace } from '@douyinfe/semi-icons-lab';

const fontSizes = [
    { value: '12px', text: '12px' },
    { value: '14px', text: '14px' },
    { value: '16px', text: '16px' },
    { value: '18px', text: '18px' },
    { value: '20px', text: '20px' },
    { value: '24px', text: '24px' },
    { value: '30px', text: '30px' },
    { value: '36px', text: '36px' },
    { value: '48px', text: '48px' },
];

const colors = [
    'transparent', '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
    '#ff0000', '#ff4d4f', '#ff7875', '#ff9c9c', '#ffb5b5', '#ffccc7', '#ffe2dd', '#fff1f0',
    '#0050b3', '#096dd9', '#1890ff', '#40a9ff', '#69c0ff', '#91d5ff', '#bae7ff', '#e6f7ff',
    '#237804', '#389e0d', '#52c41a', '#73d13d', '#95de64', '#b7eb8f', '#d9f7be', '#f6ffed',
];

const lineHeights = [
    { value: '1', text: '1' },
    { value: '1.15', text: '1.15' },
    { value: '1.5', text: '1.5' },
    { value: '2', text: '2' },
    { value: '2.5', text: '2.5' },
    { value: '3', text: '3' },
];

const letterSpacings = [
    { value: '0px', text: '默认' },
    { value: '1px', text: '1px' },
    { value: '2px', text: '2px' },
    { value: '3px', text: '3px' },
    { value: '4px', text: '4px' },
    { value: '5px', text: '5px' },
];

// 创建字间距扩展
const LetterSpacing = Extension.create({
    name: 'letterSpacing',
    addGlobalAttributes() {
        return [{
            types: ['paragraph', 'heading'],
            attributes: {
                letterSpacing: {
                    default: '0px',
                    parseHTML: element => element.style.letterSpacing,
                    renderHTML: attributes => {
                        if (!attributes.letterSpacing) return {};
                        return { style: `letter-spacing: ${attributes.letterSpacing}` };
                    },
                },
            }
        }];
    },
    addCommands() {
        return {
            setLetterSpacing: (value) => ({ chain }) => {
                return chain()
                    .updateAttributes('paragraph', { letterSpacing: value })
                    .updateAttributes('heading', { letterSpacing: value })
                    .run();
            },
        };
    },
});

// 自定义图片扩展
const ResizableImage = Extension.create({
    name: 'resizableImage',
    addOptions() {
        return {
            HTMLAttributes: {
                class: 'resizable-image'
            },
        }
    },
    addGlobalAttributes() {
        return [
            {
                types: ['image'],
                attributes: {
                    width: {
                        default: null,
                        parseHTML: element => element.getAttribute('width'),
                        renderHTML: attributes => {
                            if (!attributes.width) return {};
                            return { width: attributes.width };
                        },
                    },
                    height: {
                        default: null,
                        parseHTML: element => element.getAttribute('height'),
                        renderHTML: attributes => {
                            if (!attributes.height) return {};
                            return { height: attributes.height };
                        },
                    },
                    style: {
                        default: null,
                        parseHTML: element => element.getAttribute('style'),
                        renderHTML: attributes => {
                            if (!attributes.style) return {};
                            return { style: attributes.style };
                        },
                    },
                },
            },
        ]
    },
    addCommands() {
        return {
            setImageSize: (width, height) => ({ commands }) => {
                return commands.updateAttributes('image', { width, height });
            },
            setImageAlignment: (alignment) => ({ commands }) => {
                return commands.updateAttributes('image', {
                    style: `display: block; margin: 0 auto; text-align: ${alignment};`
                });
            },
        }
    },
    addNodeView() {
        return ({ node, HTMLAttributes, getPos, editor }) => {
            const dom = document.createElement('div');
            dom.className = 'resizable-image';
            dom.style.display = 'inline-block';
            dom.style.position = 'relative';

            const img = document.createElement('img');
            Object.entries(HTMLAttributes).forEach(([key, value]) => {
                img.setAttribute(key, value);
            });

            dom.appendChild(img);

            // 调整大小的逻辑
            const startResizing = (e) => {
                e.preventDefault();
                e.stopPropagation();

                const startX = e.clientX;
                const startY = e.clientY;
                const startWidth = img.width;
                const startHeight = img.height;

                const onMouseMove = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const deltaX = e.clientX - startX;
                    const deltaY = e.clientY - startY;
                    const newWidth = Math.max(50, startWidth + deltaX);
                    const newHeight = Math.max(50, startHeight + deltaY);

                    img.style.width = `${newWidth}px`;
                    img.style.height = `${newHeight}px`;
                };

                const onMouseUp = () => {
                    document.removeEventListener('mousemove', onMouseMove);
                    document.removeEventListener('mouseup', onMouseUp);

                    if (getPos) {
                        editor.commands.setImageSize(img.width, img.height);
                    }
                };

                document.addEventListener('mousemove', onMouseMove);
                document.addEventListener('mouseup', onMouseUp);
            };

            dom.addEventListener('mousedown', startResizing);

            return {
                dom,
                update: (node) => {
                    Object.entries(node.attrs).forEach(([key, value]) => {
                        img.setAttribute(key, value);
                    });
                    return true;
                },
            };
        };
    },
});

const MenuBar = ({ editor, isSourceMode, setIsSourceMode }) => {
    const [historyColors, setHistoryColors] = React.useState(() => {
        const saved = localStorage.getItem('richEditorHistoryColors');
        return saved ? JSON.parse(saved) : [];
    });
    const [historyBgColors, setHistoryBgColors] = React.useState(() => {
        const saved = localStorage.getItem('richEditorHistoryBgColors');
        return saved ? JSON.parse(saved) : [];
    });

    const handleColorSelect = (color) => {
        editor.chain().focus().setColor(color).run();
        if (!historyColors.includes(color)) {
            const newHistoryColors = [color, ...historyColors.slice(0, 9)];
            setHistoryColors(newHistoryColors);
            localStorage.setItem('richEditorHistoryColors', JSON.stringify(newHistoryColors));
        }
    };

    const handleBgColorSelect = (color) => {
        editor.chain().focus().toggleHighlight({ color }).run();
        if (!historyBgColors.includes(color)) {
            const newHistoryBgColors = [color, ...historyBgColors.slice(0, 9)];
            setHistoryBgColors(newHistoryBgColors);
            localStorage.setItem('richEditorHistoryBgColors', JSON.stringify(newHistoryBgColors));
        }
    };

    if (!editor) return null;

    return (
        <div className="editor-menu-bar">
            <ButtonGroup>
                <Tooltip content="加粗">
                    <Button
                        icon={<IconBold />}
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        type={editor.isActive('bold') ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="斜体">
                    <Button
                        icon={<IconItalic />}
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        type={editor.isActive('italic') ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="下划线">
                    <Button
                        icon={<IconUnderline />}
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        type={editor.isActive('underline') ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="删除线">
                    <Button
                        icon={<IconStrikeThrough />}
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        type={editor.isActive('strike') ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="字号">
                    <Popover
                        showArrow
                        trigger="click"
                        position="bottomLeft"
                        content={
                            <div style={{ padding: '8px', minWidth: '120px' }}>
                                {fontSizes.map(size => (
                                    <div
                                        key={size.value}
                                        style={{
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            backgroundColor: editor.getAttributes('textStyle').fontSize === size.value ? 'var(--semi-color-fill-0)' : 'transparent',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => editor.chain().focus().setFontSize(size.value).run()}
                                    >
                                        {size.text}
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Button
                            icon={<IconHn />}
                            type={editor.getAttributes('textStyle').fontSize ? 'primary' : 'tertiary'}
                        />
                    </Popover>
                </Tooltip>
                <Tooltip content="文字颜色">
                    <Popover
                        showArrow
                        trigger="click"
                        position="bottomLeft"
                        content={
                            <div style={{ display: 'grid', gap: '8px', padding: '8px', width: '240px' }}>
                                {historyColors.length > 0 && (
                                    <div>
                                        <div style={{ marginBottom: '4px', color: 'var(--semi-color-text-2)' }}>最近使用</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px' }}>
                                            {historyColors.map((color, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        width: '24px',
                                                        height: '24px',
                                                        backgroundColor: color,
                                                        cursor: 'pointer',
                                                        border: '1px solid var(--semi-color-border)',
                                                        borderRadius: '2px',
                                                        boxShadow: editor.getAttributes('textStyle').color === color ? '0 0 0 2px var(--semi-color-primary)' : 'none'
                                                    }}
                                                    onClick={() => handleColorSelect(color)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <div style={{ marginBottom: '4px', color: 'var(--semi-color-text-2)' }}>预设颜色</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px' }}>
                                        {colors.map((color, index) => (
                                            <ColorButton
                                                key={index}
                                                color={color}
                                                isActive={editor.getAttributes('textStyle').color === color}
                                                onClick={() => handleColorSelect(color)}
                                                isTransparent={color === 'transparent'}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                                    <Input
                                        type="color"
                                        placeholder="输入十六进制颜色值"
                                        onChange={value => handleColorSelect(value)}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        }
                    >
                        <Button
                            icon={<IconFontColor />}
                            type={editor.isActive('textStyle') ? 'primary' : 'tertiary'}
                            style={{
                                backgroundColor: editor.getAttributes('textStyle').color || 'transparent',
                                borderColor: editor.isActive('textStyle') ? 'var(--semi-color-primary)' : 'var(--semi-color-border)'
                            }}
                        />
                    </Popover>
                </Tooltip>
                <Tooltip content="背景颜色">
                    <Popover
                        showArrow
                        trigger="click"
                        position="bottomLeft"
                        content={
                            <div style={{ display: 'grid', gap: '8px', padding: '8px', width: '240px' }}>
                                {historyBgColors.length > 0 && (
                                    <div>
                                        <div style={{ marginBottom: '4px', color: 'var(--semi-color-text-2)' }}>最近使用</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px' }}>
                                            {historyBgColors.map((color, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        width: '24px',
                                                        height: '24px',
                                                        backgroundColor: color,
                                                        cursor: 'pointer',
                                                        border: '1px solid var(--semi-color-border)',
                                                        borderRadius: '2px',
                                                        boxShadow: editor.isActive('highlight', { color }) ? '0 0 0 2px var(--semi-color-primary)' : 'none'
                                                    }}
                                                    onClick={() => handleBgColorSelect(color)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div>
                                    <div style={{ marginBottom: '4px', color: 'var(--semi-color-text-2)' }}>预设颜色</div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '4px' }}>
                                        {colors.map((color, index) => (
                                            <ColorButton
                                                key={index}
                                                color={color}
                                                isActive={editor.isActive('highlight', { color })}
                                                onClick={() => handleBgColorSelect(color)}
                                                isTransparent={color === 'transparent'}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div style={{ gridColumn: '1 / -1', marginTop: '8px' }}>
                                    <Input
                                        type="color"
                                        placeholder="输入十六进制颜色值"
                                        onChange={value => handleBgColorSelect(value)}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>
                        }
                    >
                        <Button
                            icon={<IconMark />}
                            type={editor.isActive('highlight') ? 'primary' : 'tertiary'}
                            style={{
                                backgroundColor: editor.getAttributes('highlight').color || 'transparent',
                                borderColor: editor.isActive('highlight') ? 'var(--semi-color-primary)' : 'var(--semi-color-border)'
                            }}
                        />
                    </Popover>
                </Tooltip>
            </ButtonGroup>

            <ButtonGroup style={{ marginLeft: 8 }}>
                <Tooltip content="左对齐">
                    <Button
                        icon={<IconAlignLeft />}
                        onClick={() => {
                            if (editor.isActive('image')) {
                                editor.chain().focus().setImageAlignment('left').run();
                            } else {
                                editor.chain().focus().setTextAlign('left').run();
                            }
                        }}
                        type={editor.isActive({ textAlign: 'left' }) || (editor.isActive('image') && editor.getAttributes('image').style?.includes('text-align: left')) ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="居中对齐">
                    <Button
                        icon={<IconAlignCenter />}
                        onClick={() => {
                            if (editor.isActive('image')) {
                                editor.chain().focus().setImageAlignment('center').run();
                            } else {
                                editor.chain().focus().setTextAlign('center').run();
                            }
                        }}
                        type={editor.isActive({ textAlign: 'center' }) || (editor.isActive('image') && editor.getAttributes('image').style?.includes('text-align: center')) ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
                <Tooltip content="右对齐">
                    <Button
                        icon={<IconAlignRight />}
                        onClick={() => {
                            if (editor.isActive('image')) {
                                editor.chain().focus().setImageAlignment('right').run();
                            } else {
                                editor.chain().focus().setTextAlign('right').run();
                            }
                        }}
                        type={editor.isActive({ textAlign: 'right' }) || (editor.isActive('image') && editor.getAttributes('image').style?.includes('text-align: right')) ? 'primary' : 'tertiary'}
                    />
                </Tooltip>
            </ButtonGroup>

            <ButtonGroup style={{ marginLeft: 8 }}>
                <Tooltip content="行高">
                    <Popover
                        showArrow
                        trigger="click"
                        position="bottomLeft"
                        content={
                            <div style={{ padding: '8px', minWidth: '120px' }}>
                                {lineHeights.map(height => (
                                    <div
                                        key={height.value}
                                        style={{
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            backgroundColor: editor.getAttributes('paragraph').lineHeight === height.value ? 'var(--semi-color-fill-0)' : 'transparent',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => editor.chain().focus().setLineHeight(height.value).run()}
                                    >
                                        {height.text}
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Button
                            icon={<IconLineHeight />}
                            type={editor.getAttributes('paragraph').lineHeight ? 'primary' : 'tertiary'}
                        />
                    </Popover>
                </Tooltip>
                <Tooltip content="字间距">
                    <Popover
                        showArrow
                        trigger="click"
                        position="bottomLeft"
                        content={
                            <div style={{ padding: '8px', minWidth: '120px' }}>
                                {letterSpacings.map(spacing => (
                                    <div
                                        key={spacing.value}
                                        style={{
                                            padding: '4px 8px',
                                            cursor: 'pointer',
                                            backgroundColor: editor.getAttributes('paragraph').letterSpacing === spacing.value ? 'var(--semi-color-fill-0)' : 'transparent',
                                            borderRadius: '4px'
                                        }}
                                        onClick={() => editor.chain().focus().setLetterSpacing(spacing.value).run()}
                                    >
                                        {spacing.text}
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Button
                            icon={<IconSpace />}
                            type={editor.getAttributes('paragraph').letterSpacing ? 'primary' : 'tertiary'}
                        />
                    </Popover>
                </Tooltip>
            </ButtonGroup>

            <Tooltip content="插入图片" style={{ marginLeft: 8 }}>
                <Button
                    icon={<IconImage />}
                    type="tertiary"
                    onClick={() => {
                        const handleChange = (url) => {
                            if (url) {
                                editor.chain().focus().insertContent({
                                    type: 'image',
                                    attrs: {
                                        src: url,
                                        class: 'resizable-image'
                                    }
                                }).run();
                            }
                        };
                        const props = {
                            onChange: handleChange,
                            width: 300,
                            previewSize: 120,
                            visible: true,
                            onClose: () => { },
                            editor: editor
                        };
                        const imageSelector = <ImageSelector {...props} />;
                        createRoot(document.createElement('div')).render(imageSelector);
                    }}
                />
            </Tooltip>

            <div style={{ flex: 1 }}></div>

            <Tooltip content={isSourceMode ? "切换到富文本模式" : "切换到源码模式"}>
                <Button
                    icon={<IconCode />}
                    type={isSourceMode ? "primary" : "tertiary"}
                    onClick={() => setIsSourceMode(!isSourceMode)}
                />
            </Tooltip>
        </div>
    );
};

// 创建行高扩展
const LineHeight = Extension.create({
    name: 'lineHeight',
    addGlobalAttributes() {
        return [{
            types: ['paragraph', 'heading', 'listItem'],
            attributes: {
                lineHeight: {
                    default: null,
                    parseHTML: element => element.style.lineHeight || null,
                    renderHTML: attributes => {
                        if (!attributes.lineHeight) return {};
                        return { style: `line-height: ${attributes.lineHeight}` };
                    },
                },
            }
        }];
    },
    addCommands() {
        return {
            setLineHeight: (value) => ({ chain }) => {
                return chain()
                    .updateAttributes('paragraph', { lineHeight: value })
                    .updateAttributes('heading', { lineHeight: value })
                    .run();
            },
        };
    },
});

const RichEditor = ({ value, onChange, placeholder }) => {
    const [isSourceMode, setIsSourceMode] = React.useState(false);
    const [sourceContent, setSourceContent] = React.useState(value);
    const editor = useEditor({
        extensions: [
            StarterKit,
            Underline,
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            TextAlign.configure({
                types: ['paragraph', 'heading']
            }),
            FontFamily.configure({
                types: ['textStyle']
            }),
            FontSize.configure({
                types: ['textStyle']
            }),
            LineHeight,
            Image.configure({
                HTMLAttributes: {
                    class: 'resizable-image'
                }
            }),
            ResizableImage,
            LetterSpacing
        ],
        content: value,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            if (onChange) {
                onChange(html);
            }
            setSourceContent(html);
        },
        editorProps: {
            attributes: {
                class: 'rich-editor',
                placeholder: placeholder
            }
        }
    });

    React.useEffect(() => {
        if (editor && value !== editor.getHTML()) {
            editor.commands.setContent(value);
            setSourceContent(value);
        }
    }, [value, editor]);

    const handleSourceChange = (e) => {
        const newContent = e.target.value;
        setSourceContent(newContent);
        if (onChange) {
            onChange(newContent);
        }
    };

    const handleModeChange = (newMode) => {
        if (newMode === false && editor) {
            // 从源码模式切换回富文本模式时，更新编辑器内容
            editor.commands.setContent(sourceContent);
        }
        setIsSourceMode(newMode);
    };

    return (
        <div className="rich-editor-container">
            <MenuBar editor={editor} isSourceMode={isSourceMode} setIsSourceMode={handleModeChange} />
            {isSourceMode ? (
                <textarea
                    className="source-editor"
                    value={sourceContent}
                    onChange={handleSourceChange}
                    placeholder={placeholder}
                />
            ) : (
                <EditorContent editor={editor} />
            )}
        </div>
    );
};

export default RichEditor;

const ColorButton = ({ color, isActive, onClick, isTransparent }) => (
    <div
        style={{
            width: '24px',
            height: '24px',
            backgroundColor: color,
            cursor: 'pointer',
            border: '1px solid var(--semi-color-border)',
            borderRadius: '2px',
            boxShadow: isActive ? '0 0 0 2px var(--semi-color-primary)' : 'none',
            position: 'relative',
            overflow: 'hidden'
        }}
        onClick={onClick}
        title={color}
    >
        {isTransparent && (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc), linear-gradient(45deg, #ccc 25%, transparent 25%, transparent 75%, #ccc 75%, #ccc)',
                backgroundSize: '6px 6px',
                backgroundPosition: '0 0, 3px 3px'
            }} />
        )}
    </div>
);